import { Fragment, ReactNode } from "react";
import {
  CircleMinusIcon,
  SignpostIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  WrenchIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { dealScoreTone } from "@/app/Relocations/Utils/dealScoreTone";

type Impact = "high" | "medium" | "low";

type Block =
  | { kind: "verdict"; rating: number | null; likelihood: string | null }
  | { kind: "heading"; text: string }
  | { kind: "bullet"; text: string }
  | { kind: "action"; position: number; text: string; impact: Impact | null }
  | { kind: "paragraph"; text: string };

const IMPACT_STYLE: Record<Impact, string> = {
  high: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  medium:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  low: "bg-zinc-100 text-zinc-600 dark:bg-white/10 dark:text-zinc-300",
};

const LIKELIHOOD_STYLE: Record<string, string> = {
  likely:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  possible:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  unlikely: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

const LIKELIHOOD_ICON: Record<
  string,
  typeof TrendingUpIcon | typeof CircleMinusIcon
> = {
  likely: TrendingUpIcon,
  possible: CircleMinusIcon,
  unlikely: TrendingDownIcon,
};

/**
 * Renders the fixed shape the deal analysis agent answers in: a scored verdict,
 * one-line reasons, then numbered actions carrying an impact tag. Anything that
 * does not match falls back to plain text, so a stray answer still reads.
 *
 * Deliberately static: a streamed answer is swapped for its stored copy the
 * moment it finishes, and any entrance animation would replay on that swap.
 */
export function DealAnalysisAnswer({ content }: { content: string }) {
  return (
    <div className="space-y-2.5">
      {withoutEmptySections(parse(content)).map((block, index) => (
        <Fragment key={index}>{renderBlock(block)}</Fragment>
      ))}
    </div>
  );
}

/**
 * Drops a heading with nothing under it. A deal with no action worth taking ends
 * at the reasons, and an answer that still emits a bare "Do this" should not
 * draw a section around empty space.
 */
function withoutEmptySections(blocks: Block[]): Block[] {
  return blocks.filter(
    (block, index) =>
      block.kind !== "heading" ||
      (blocks[index + 1] !== undefined && blocks[index + 1].kind !== "heading"),
  );
}

function renderBlock(block: Block): ReactNode {
  switch (block.kind) {
    case "verdict":
      return <Verdict rating={block.rating} likelihood={block.likelihood} />;

    case "heading": {
      // The score and the likelihood are separate reads, so the actions heading
      // and the will-it-book heading should not look like the same section.
      const Icon = /do this/i.test(block.text) ? WrenchIcon : SignpostIcon;

      return (
        <div className="flex items-center gap-2 pt-1">
          <Icon className="h-3.5 w-3.5 text-zinc-400" />
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {block.text}
          </p>
          <span className="h-px flex-1 bg-zinc-950/5 dark:bg-white/10" />
        </div>
      );
    }

    case "bullet":
      return (
        <div className="flex gap-2.5 text-sm/6 text-zinc-700 dark:text-zinc-300">
          <span
            aria-hidden
            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300 dark:bg-white/25"
          />
          <span>{inline(block.text)}</span>
        </div>
      );

    case "action":
      return (
        <div className="flex items-start gap-3 rounded-xl border border-zinc-950/5 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-xs font-bold text-white shadow-sm">
            {block.position}
          </span>
          <p className="flex-1 text-sm/6 text-zinc-900 dark:text-white">
            {inline(block.text)}
          </p>
          {block.impact !== null ? (
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                IMPACT_STYLE[block.impact],
              )}
            >
              {block.impact}
            </span>
          ) : null}
        </div>
      );

    case "paragraph":
      return (
        <p className="whitespace-pre-wrap text-sm/6 text-zinc-700 dark:text-zinc-300">
          {inline(block.text)}
        </p>
      );
  }
}

function Verdict({
  rating,
  likelihood,
}: {
  rating: number | null;
  likelihood: string | null;
}) {
  const Icon = likelihood !== null ? LIKELIHOOD_ICON[likelihood] : null;
  const tone = dealScoreTone(rating);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-zinc-950/5 bg-white/70 px-4 py-3 dark:border-white/10 dark:bg-white/5">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Deal score
        </p>
        {rating !== null ? (
          <>
            <p className="flex items-baseline gap-1">
              <span
                className={cn("text-3xl font-bold leading-none", tone.text)}
              >
                {rating}
              </span>
              <span className="text-sm font-medium text-zinc-400">/10</span>
            </p>
            <ScoreMeter rating={rating} tone={tone.fill} />
          </>
        ) : null}
      </div>

      {likelihood !== null ? (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold",
            LIKELIHOOD_STYLE[likelihood] ?? LIKELIHOOD_STYLE.possible,
          )}
        >
          {Icon !== null ? <Icon className="h-4 w-4" /> : null}
          {likelihood} to book
        </span>
      ) : null}
    </div>
  );
}

function ScoreMeter({ rating, tone }: { rating: number; tone: string }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 10 }, (_, segment) => (
        <span
          key={segment}
          className={cn(
            "h-2 w-4 rounded-full",
            segment < Math.round(rating)
              ? tone
              : "bg-zinc-200 dark:bg-white/10",
          )}
        />
      ))}
    </div>
  );
}

function parse(content: string): Block[] {
  const blocks: Block[] = [];

  for (const raw of content.split("\n")) {
    const line = raw.trim();

    if (line === "") {
      continue;
    }

    const verdict = line.match(
      /^\*\*verdict\*\*\s*:?\s*(\d+(?:\.\d+)?)?\s*\/?\s*(?:10)?\s*·?\s*(unlikely|possible|likely)?/i,
    );

    if (verdict !== null) {
      blocks.push({
        kind: "verdict",
        rating: verdict[1] !== undefined ? Number(verdict[1]) : null,
        likelihood: verdict[2]?.toLowerCase() ?? null,
      });
      continue;
    }

    const heading = line.match(/^\*\*(.+?)\*\*:?$/);

    if (heading !== null) {
      blocks.push({ kind: "heading", text: heading[1] });
      continue;
    }

    const action = line.match(/^(\d+)[.)]\s+(.*)$/);

    if (action !== null) {
      const { text, impact } = extractImpact(action[2]);
      blocks.push({
        kind: "action",
        position: Number(action[1]),
        text,
        impact,
      });
      continue;
    }

    const bullet = line.match(/^[-*•]\s+(.*)$/);

    if (bullet !== null) {
      blocks.push({ kind: "bullet", text: bullet[1] });
      continue;
    }

    blocks.push({ kind: "paragraph", text: line });
  }

  return blocks;
}

function extractImpact(text: string): { text: string; impact: Impact | null } {
  const tagged = text.match(/\((high|medium|low)\s*impact\)\s*$/i);

  if (tagged === null) {
    return { text, impact: null };
  }

  return {
    text: text.slice(0, tagged.index).trim(),
    impact: tagged[1].toLowerCase() as Impact,
  };
}

/** Renders `**bold**` runs; everything else is plain text. */
function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong
        key={index}
        className="font-semibold text-zinc-950 dark:text-white"
      >
        {part.slice(2, -2)}
      </strong>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}
